import type { CSSProperties } from 'react';

import type { RolesResponsibilitiesContent } from '@/blocks/domain/types';
import type { RendererProps } from '../../domain/RendererContract';
import { customerVisibleRows } from '@/common/domain/utils/customerVisibleRows';
import { BORDER_COLORS, TEXT_COLORS } from '@/common/presentation/constants/colors';
import { resolveTokens } from '../../application/resolveTokens';
import { rolesUseCategories } from '@/blocks/domain/helpers/roleCategories';
import { RolesCategoryView } from './roles/RolesCategoryView';
import { RolesQuoteGroup } from './roles/RolesQuoteGroup';
import { toRoleCategories } from '@/blocks/domain/helpers/roleCategories';
import { useQuoteCanvasEditing } from '@/document-render/presentation/editing/quote/QuoteCanvasEditingContext';
import { useDocumentAudience } from '@/document-render/presentation/primitives/useDocumentAudience';

type Row = RolesResponsibilitiesContent['rows'][number];

const defaultRoleColumns = () => ({
  role: { visible: true, label: 'Role' },
  responsibility: { visible: true, label: 'Responsibility' }
});

const TABLE_HEADER_CELL_STYLE: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 10,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: TEXT_COLORS.ON_FAINT
};

const TABLE_BODY_CELL_STYLE: CSSProperties = {
  padding: '8px 10px',
  verticalAlign: 'top'
};

export function Header(props: RendererProps<RolesResponsibilitiesContent>) {
  const { content } = props;
  const columns = content.columns ?? defaultRoleColumns();
  const cols: Array<'role' | 'responsibility'> = [];
  if (columns.role.visible) cols.push('role');
  if (columns.responsibility.visible) cols.push('responsibility');

  return (
    <thead>
      <tr style={{ background: BORDER_COLORS.FAINT }}>
        {cols.map((c) => (
          <th key={c} style={TABLE_HEADER_CELL_STYLE}>
            {c === 'role' ? columns.role.label : columns.responsibility.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

export function Component(props: RendererProps<RolesResponsibilitiesContent>) {
  const quote = useQuoteCanvasEditing();
  const audience = useDocumentAudience();
  const { content, context, style, slice, knownPaths } = props;
  const resolveOptions = knownPaths ? { knownPaths } : undefined;
  const allRows = content.rows ?? [];
  const columns = content.columns ?? defaultRoleColumns();
  const rolesEditing = quote?.kind === 'roles' ? quote : null;
  const showQuoteEditor = rolesEditing != null && audience !== 'customer';
  const resolvedTitle = content.title ? resolveTokens(content.title, context, resolveOptions).trim() : '';

  if (rolesUseCategories(content) && content.categories) {
    if (showQuoteEditor && rolesEditing) {
      return <RolesQuoteGroup categories={content.categories} editing={rolesEditing.editing} />;
    }
    return (
      <RolesCategoryView categories={content.categories} context={context} title={resolvedTitle} style={style} knownPaths={knownPaths} />
    );
  }

  if (showQuoteEditor && rolesEditing) {
    return (
      <section aria-label="block-roles-responsibilities">
        {resolvedTitle ? <div style={{ fontWeight: 700, marginBottom: 6 }}>{resolvedTitle}</div> : null}
        <RolesQuoteGroup categories={toRoleCategories(allRows)} editing={rolesEditing.editing} />
      </section>
    );
  }

  const cols: Array<'role' | 'responsibility'> = [];
  if (columns.role.visible) cols.push('role');
  if (columns.responsibility.visible) cols.push('responsibility');

  const sourceRows = audience === 'customer' ? customerVisibleRows(allRows) : allRows;
  const start = slice?.startIndex ?? 0;
  const end = slice?.endIndex ?? sourceRows.length;
  const rows = sourceRows.slice(start, end);
  const shouldShowHeader = !slice || slice.startIndex === 0 || slice.isContinuation;
  const rowHeight = 20;
  const minHeight = rows.length * rowHeight + (shouldShowHeader ? rowHeight : 0);

  return (
    <section
      aria-label="block-roles-responsibilities"
      style={{
        minHeight,
        paddingTop: style.paddingY,
        paddingBottom: style.paddingY,
        color: style.textColor,
        background: style.backgroundColor,
        fontSize: style.fontSize ?? 13,
        fontWeight: style.fontWeight
      }}
    >
      {resolvedTitle ? <div style={{ fontWeight: 700, marginBottom: 6 }}>{resolvedTitle}</div> : null}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        {shouldShowHeader ? <Header {...props} /> : null}
        <tbody>
          {rows.map((row: Row, idx) => (
            <tr
              key={slice ? `${slice.startIndex + idx}` : `${idx}`}
              style={{ borderBottom: `1px solid ${BORDER_COLORS.LIGHT}`, opacity: row.hidden === true ? 0.5 : undefined }}
            >
              {cols.map((c) => (
                <td key={c} style={TABLE_BODY_CELL_STYLE}>
                  {c === 'role' ? (
                    <b>{resolveTokens(row.role ?? '', context, resolveOptions)}</b>
                  ) : (
                    resolveTokens(row.responsibility ?? '', context, resolveOptions)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export const renderer = {
  Component,
  flowBehavior: 'splittable' as const,
  Header
};

export type { RolesResponsibilitiesContent };
